'use client';

import { useState, useEffect } from 'react';
import { visitCounselApi } from './visit-counsel.api';
import { orderApi } from '@/entities/order';
import { plannerApi } from '@/entities/planner';
import type { DepartureLocationInfo } from '@/entities/order/model/order.types';

export type PlannerDayCounselType = 'current' | 'previous' | 'other';

export interface PlannerDayCounsel {
  visitCounselId: string;
  orderId: string;
  scheduleTime: string;
  location: DepartureLocationInfo | null;
  type: PlannerDayCounselType;
  /** 플래너(상담원) 이름 */
  counselorName?: string;
  /** 플래너 ID (동선 그룹핑용, 다중 플래너 지도) */
  counselorId?: string;
  /** 평수 */
  supplyArea?: number;
  /** 주문번호 (탭 링크용) */
  misoOrderId?: string;
}

export interface UsePlannerDayCounselsResult {
  counsels: PlannerDayCounsel[];
  isLoading: boolean;
  error: Error | undefined;
}

async function fetchPlannerDayCounsels(
  currentCounselId: string,
  currentOrderLocation: DepartureLocationInfo | null
): Promise<PlannerDayCounsel[]> {
  const current = await visitCounselApi.getVisitCounselById(currentCounselId);
  const { counselorId, scheduleDate, scheduleTime } = current;

  const [result, planner] = await Promise.all([
    visitCounselApi.searchVisitCounsels({
      filterGroups: [
        {
          logic: 'AND' as const,
          filters: [
            {
              field: 'counselorId' as const,
              operator: 'EQUALS' as const,
              value: counselorId,
              values: null,
            },
            {
              field: 'scheduleDate' as const,
              operator: 'EQUALS' as const,
              value: scheduleDate,
              values: null,
            },
          ],
        },
      ],
      sortBy: 'scheduleDate' as const,
      sortOrder: 'ASC' as const,
      pageSize: 50,
    }),
    plannerApi.getPlannerById(counselorId),
  ]);

  const counselorName = planner?.name ?? '';

  const sorted = [...result.items].sort((a, b) =>
    a.scheduleTime < b.scheduleTime ? -1 : a.scheduleTime > b.scheduleTime ? 1 : 0
  );

  const uniqueOrderIds = [...new Set(sorted.map((c) => c.orderId))];
  const orderMap = new Map<
    string,
    { misoOrderId: string; location: DepartureLocationInfo | null }
  >();

  if (uniqueOrderIds.length > 0) {
    const orders = await orderApi.getOrdersByIds(uniqueOrderIds);
    for (const order of orders) {
      orderMap.set(order.id, {
        misoOrderId: order.misoOrderId,
        location: order.assignedDepartureLocation ?? null,
      });
    }
  }

  const currentIndex = sorted.findIndex((c) => c.id === currentCounselId);
  const counsels: PlannerDayCounsel[] = [];

  for (let i = 0; i < sorted.length; i++) {
    const c = sorted[i]!;
    const isCurrent = c.id === currentCounselId;
    const isPrevious = currentIndex >= 0 && i === currentIndex - 1;
    const orderInfo = orderMap.get(c.orderId);

    let location: DepartureLocationInfo | null;
    if (isCurrent) {
      location = currentOrderLocation ?? orderInfo?.location ?? null;
    } else {
      location = orderInfo?.location ?? null;
    }

    counsels.push({
      visitCounselId: c.id,
      orderId: c.orderId,
      scheduleTime: c.scheduleTime,
      location,
      type: isCurrent ? 'current' : isPrevious ? 'previous' : 'other',
      counselorName,
      supplyArea: c.supplyArea,
      misoOrderId: orderInfo?.misoOrderId,
    });
  }

  return counsels;
}

/**
 * 해당 플래너의 해당 일자 전체 상담 조회 (scheduleTime 순)
 * @param currentCounselId 현재 상담 ID
 * @param currentOrderLocation 현재 주문의 출발지 좌표 (현재 상담 마커용)
 */
export function usePlannerDayCounsels(
  currentCounselId: string | null,
  currentOrderLocation: DepartureLocationInfo | null
): UsePlannerDayCounselsResult {
  const [counsels, setCounsels] = useState<PlannerDayCounsel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    if (!currentCounselId) {
      setCounsels([]);
      setError(undefined);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(undefined);

    fetchPlannerDayCounsels(currentCounselId, currentOrderLocation)
      .then((list) => {
        if (!cancelled) setCounsels(list);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('플래너 일자 상담 조회 실패'));
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentCounselId, currentOrderLocation?.longitude, currentOrderLocation?.latitude]);

  return {
    counsels,
    isLoading,
    error,
  };
}
